import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import InlineSpinner from "../../components/shared/Spinner/InlineSpinner";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";
import { deleteAgent, generateAgentPrompt, listAgents, listProviders, readFileContent, updateAgentFromDraft } from "../../api/providers";
import Loader from "../../components/Loader/Loader";
import UserAuthStore from "../../store/userStore";
import UIStore from "../../store/uiStore";
import Header from "../../components/Header/Header";
import { CompactButton, CompactDangerButton, PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import FileUploadInput from "../../components/Inputs/FileUploadInput";
import Input from "../../components/Inputs/Input";
import TextArea from "../../components/Inputs/TextArea";
import ROUTES from "../../router/routes";
import FileEditorSection from "../../components/Forge/FileEditorSection";
import EntityAuditStrip from "../../components/Forge/EntityAuditStrip";
import AgentSkillsSection from "../../components/Forge/AgentSkillsSection/AgentSkillsSection";
import AgentToolDiscoveryPanel from "../../components/Forge/AgentToolDiscoveryPanel";
import AgentAuthSection from "../../components/Forge/AgentAuthSection";
import AgentConnectionSection from "../../components/Forge/AgentConnectionSection";
import {
  createConnectionDrafts,
  getConnectionProtocol,
} from "../../components/Forge/connectionProtocols";
import DraftStatusBadge from "../../components/Forge/DraftStatusBadge";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import Toggle from "../../components/shared/Toggle";
import { useFormik } from "formik";
import * as Yup from "yup";
import logger from "../../services/Logger";
import { isDirty } from "../../utils/isDirty";
import {
  CONNECTION_FIELDS,
  EMPTY_CONNECTION,
  pickConnection,
  readConnectionFromPolicy,
  writeConnectionToPolicy,
} from "../../utils/agentPolicyConnection";
import { mergeDiscoveredToolDrafts, summarizeDraftChanges } from "../../utils/agentToolDrafts";
import useAgentToolDiscovery from "../../hooks/useAgentToolDiscovery";

const FIELD_LABEL_CLASS = "block text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5";
const HELP_TEXT_CLASS = "text-[11px] text-ink-gray-400 mt-1";
const SECTION_HEADING_CLASS = "text-sm font-semibold text-ink-gray-900 dark:text-ink-white mb-4";

const EditAgentPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { agentId } = useParams();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const isAdmin = UIStore((state) => state.orgAvailable) === true;
  // Admins must be able to open unpublished agents to re-publish them.
  const agentListOptions = useMemo(
    () => ({ uid: user?.uid, includeDisabled: isAdmin }),
    [isAdmin, user?.uid]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionType, setConnectionType] = useState("mcp");
  const [originalConnectionType, setOriginalConnectionType] = useState("mcp");
  const [connectionDrafts, setConnectionDrafts] = useState(createConnectionDrafts);
  const [originalConnectionDraft, setOriginalConnectionDraft] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [agentAudit, setAgentAudit] = useState(null);
  const [policyAtom, setPolicyAtom] = useState(null);
  // Snapshot of the loaded agent's form values, used to gate the Save button
  // on actual changes. NOT the same as formik.dirty: enableReinitialize only
  // re-syncs values against the static initialValues literal below (which
  // never changes) — the real agent data is loaded imperatively via setValues,
  // so formik.dirty would incorrectly read true immediately after load.
  const [originalValues, setOriginalValues] = useState(null);

  // Editor state for policy, tools, prompt
  const [editingFile, setEditingFile] = useState(null); // 'policy', 'tools', 'prompt'
  const [fileContent, setFileContent] = useState("");
  const [originalFileContent, setOriginalFileContent] = useState("");
  const [fileChangeDetection, setFileChangeDetection] = useState({});
  const [fileError, setFileError] = useState(null);
  const [jsonError, setJsonError] = useState(null);
  const [draftContents, setDraftContents] = useState({ policy: "", tools: "", prompt: "" });
  const [pendingFileBaselines, setPendingFileBaselines] = useState({});
  const [originalDraftContents, setOriginalDraftContents] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconAction, setIconAction] = useState("retain");
  const [generationBaseDrafts, setGenerationBaseDrafts] = useState(null);
  const [generationRollback, setGenerationRollback] = useState(null);
  const { discover: discoverTools, isDiscovering, progressLabel: discoveryProgressLabel } = useAgentToolDiscovery();
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [discoveredTools, setDiscoveredTools] = useState(null);
  const [selectedToolNames, setSelectedToolNames] = useState([]);
  const [discoveryError, setDiscoveryError] = useState(null);
  const [toolSearch, setToolSearch] = useState("");
  // Off by default: overwriting a hand-written prompt must be an explicit choice.
  const [regeneratePrompt, setRegeneratePrompt] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);

  const filteredDiscoveredTools = useMemo(() => {
    if (!discoveredTools) return [];
    const query = toolSearch.trim().toLowerCase();
    if (!query) return discoveredTools;
    return discoveredTools.filter((tool) => (
      tool.name.toLowerCase().includes(query)
      || (tool.description || "").toLowerCase().includes(query)
    ));
  }, [discoveredTools, toolSearch]);
  const allFilteredSelected = filteredDiscoveredTools.length > 0
    && filteredDiscoveredTools.every((tool) => selectedToolNames.includes(tool.name));
  const someFilteredSelected = filteredDiscoveredTools.some((tool) => selectedToolNames.includes(tool.name));
  // Mirrors the prompt baseline handleGenerateToolDrafts will actually use.
  const promptDraftForGeneration = generationBaseDrafts
    ? generationBaseDrafts.prompt
    : (editingFile === "prompt" ? fileContent : draftContents.prompt);
  const currentFileHasGeneratedChanges = Boolean(
    editingFile
    && generationRollback
    && generationRollback.pendingFiles.includes(editingFile)
  );
  const editorIsDirty = Boolean(editingFile) && (
    fileContent !== originalFileContent || currentFileHasGeneratedChanges
  );
  const hasPendingEditorTransaction = editorIsDirty
    || Boolean(generationRollback)
    || Object.keys(pendingFileBaselines).length > 0;

  // In-memory copy of tools.json for skills management
  const [toolsContent, setToolsContent] = useState("");
  // In-memory copy of policy.json, so main-form connection edits can be merged
  // into the real document instead of rewriting it from form state alone.
  const [policyContent, setPolicyContent] = useState("");
  // Latest known connection. The agent-load effect can run more than once while
  // policy.json is still in flight, and it rebuilds the whole values object —
  // without this the second pass would blank the Connection section.
  const connectionRef = useRef(EMPTY_CONNECTION);
  const isSwarmAgent = useMemo(() => {
    if (!toolsContent) return false;
    try { return "agents" in JSON.parse(toolsContent); } catch { return false; }
  }, [toolsContent]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Display Name is required"),
    provider: Yup.string()
      .required("Provider is required"),
    triggers: Yup.string()
      .trim()
      .required("At least one trigger is required"),
    policy_path: Yup.string()
      .required("Policy file is required"),
    tools_path: Yup.string()
      .required("Tools file is required"),
    prompt_path: Yup.string()
      .required("Prompt file is required"),
    mcp_connection: Yup.string()
      .trim()
      .url("Enter a valid URL (e.g. https://mcp.example.com/mcp)"),
  });

  const formik = useFormik({
    initialValues: {
      agent_id: "",
      name: "",
      triggers: "",
      description: "",
      service_type: "",
      provider: "",
      icon_path: "",
      policy_path: "",
      tools_path: "",
      prompt_path: "",
      enabled: true,
      mcp_connection: "",
      mcp_auth_token: "",
      auth_format: "raw",
      auth_header: "",
    },
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    submitCount,
  } = formik;

  // Fetch providers and agents
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const providersList = await listProviders(orgId);
        setProviders(providersList);
        
        // Fetch available agents for swarm agent selection
        const agentsList = await listAgents(orgId, agentListOptions);
        setAvailableAgents(agentsList);
      } catch (err) {
        logger.error('EditAgentPage', 'Failed to fetch providers', { error: err.message });
      } finally {
        setLoadingProviders(false);
      }
    };

    if (orgId) {
      fetchProviders();
    }
  }, [orgId, agentListOptions]);

  // Load existing agent data
  useEffect(() => {
    const fetchAgent = async () => {
      try {
        setIsLoading(true);
        const agents = await listAgents(orgId, agentListOptions);
        const agent = agents.find((a) => a.agent_id === agentId);

        if (agent) {
          // Convert triggers array to comma-separated string
          const triggersStr = Array.isArray(agent.triggers)
            ? agent.triggers.join(", ")
            : agent.triggers || "";
          
          const loadedValues = {
            agent_id: agent.agent_id || "",
            name: agent.name || "",
            triggers: triggersStr,
            description: agent.description || "",
            service_type: agent.service_type || "",
            provider: agent.provider || "",
            icon_path: agent.icon_path || "",
            policy_path: agent.policy_path || "",
            tools_path: agent.tools_path || "",
            prompt_path: agent.prompt_path || "",
            connection_type: agent.connection_type || "mcp",
            connection_configuration_path: agent.connection_configuration_path || "",
            enabled: agent.enabled !== false,
            ...connectionRef.current,
          };
          setValues(loadedValues);
          setOriginalValues(loadedValues);

          setAgentAudit({
            created_by: agent.created_by || null,
            created_by_name: agent.created_by_name || null,
            created_by_email: agent.created_by_email || null,
            created_at: agent.created_at || null,
            updated_by: agent.updated_by || null,
            updated_by_name: agent.updated_by_name || null,
            updated_by_email: agent.updated_by_email || null,
            updated_at: agent.updated_at || null
          });
          setPolicyAtom(agent.agent_atom || null);

          const [policyResult, toolsResult, promptResult, connectionResult] = await Promise.all([
            readFileContent(agent.policy_path, orgId),
            readFileContent(agent.tools_path, orgId),
            readFileContent(agent.prompt_path, orgId),
            agent.connection_configuration_path
              ? readFileContent(agent.connection_configuration_path, orgId)
              : Promise.resolve(null),
          ]);
          const formatJson = (content) => {
            try { return JSON.stringify(JSON.parse(content), null, 2); } catch { return content; }
          };
          const loadedDrafts = {
            policy: formatJson(policyResult.content),
            tools: formatJson(toolsResult.content),
            prompt: promptResult.content,
          };
          setDraftContents(loadedDrafts);
          setOriginalDraftContents(loadedDrafts);
          setPolicyContent(loadedDrafts.policy);
          setToolsContent(loadedDrafts.tools);

          const loadedConnectionType = agent.connection_type || "mcp";
          if (connectionResult) {
            const loadedConnectionDraft = getConnectionProtocol(
              loadedConnectionType
            ).parseConfiguration(JSON.parse(connectionResult.content));
            setConnectionDrafts((current) => ({
              ...current,
              [loadedConnectionType]: loadedConnectionDraft,
            }));
            setOriginalConnectionDraft(loadedConnectionDraft);
            setConnectionType(loadedConnectionType);
            setOriginalConnectionType(loadedConnectionType);
          } else if (loadedConnectionType === "mcp") {
            setConnectionDrafts(createConnectionDrafts());
            setOriginalConnectionDraft(null);
            setConnectionType("mcp");
            setOriginalConnectionType("mcp");
          } else {
            throw new Error("Saved connection configuration is missing");
          }

          const connection = readConnectionFromPolicy(loadedDrafts.policy);
          if (connection) {
            connectionRef.current = connection;
            CONNECTION_FIELDS.forEach((field) => setFieldValue(field, connection[field]));
            setOriginalValues((current) => (current ? { ...current, ...connection } : current));
          }
        } else {
          setError("Agent not found");
        }
      } catch (err) {
        setError("Failed to load agent");
        logger.error('EditAgentPage', 'Failed to load agent', { error: err.message, agentId });
      } finally {
        setIsLoading(false);
      }
    };

    if (orgId && agentId) {
      fetchAgent();
    }
  }, [orgId, agentId, agentListOptions, setValues, setFieldValue]);

  // policy.json remains authoritative; main-form connection edits are merged into
  // the loaded document and written back when the agent is saved.
  const resetDiscovery = () => {
    setDiscoveredTools(null);
    setSelectedToolNames([]);
    setToolSearch("");
    setRegeneratePrompt(false);
    setGenerationResult(null);
  };

  const handleConnectionTypeChange = (nextType) => {
    setConnectionType(nextType);
    resetDiscovery();
    setDiscoveryError(null);
  };

  const handleProtocolDraftChange = (type, update) => {
    setConnectionDrafts((current) => ({
      ...current,
      [type]: typeof update === "function" ? update(current[type]) : update,
    }));
    resetDiscovery();
    setDiscoveryError(null);
  };

  const handleConnectionChange = (field, value) => handleConnectionPatch({ [field]: value });

  // Generated changes stay "pending" until the file is opened and saved, so they
  // outrank the plain modified state even though both are unsaved.
  const draftStatus = (fileType) => {
    if (generationRollback?.pendingFiles.includes(fileType)) return "generated";
    if (originalDraftContents && draftContents[fileType] !== originalDraftContents[fileType]) return "modified";
    return null;
  };

  // Patches several connection fields at once; applying them one at a time would
  // rebuild the policy from a stale `values` for every field after the first.
  function handleConnectionPatch(patch) {
    Object.entries(patch).forEach(([field, value]) => setFieldValue(field, value));
    const connection = { ...pickConnection(values), ...patch };
    connectionRef.current = connection;
    setPolicyContent((current) => writeConnectionToPolicy(current, connection, values.provider));
    setDraftContents((current) => ({
      ...current,
      policy: writeConnectionToPolicy(current.policy, connection, values.provider),
    }));
    setGenerationRollback((current) => current ? {
      ...current,
      drafts: {
        ...current.drafts,
        policy: writeConnectionToPolicy(current.drafts.policy, connection, values.provider),
      },
    } : current);
    if (editingFile === "policy") {
      // Already written to draftContents above, so shift the baseline by the same
      // transform — otherwise the editor reports a change Discard cannot revert,
      // while still flagging any genuine unapplied edits in the buffer.
      setFileContent((current) => writeConnectionToPolicy(current, connection, values.provider));
      setOriginalFileContent((current) => writeConnectionToPolicy(current, connection, values.provider));
    }
    resetDiscovery();
    setDiscoveryError(null);
  }

  const handleDiscoverTools = async () => {
    setDiscoveryError(null);
    setGenerationResult(null);
    const protocol = getConnectionProtocol(connectionType);
    const discovery = protocol.discovery;
    const context = {
      provider: values.provider,
      connection: pickConnection(values),
      draft: connectionDrafts[connectionType] || null,
    };
    try {
      for (const field of discovery.validationFields) {
        await validationSchema.validateAt(field, values);
      }
      const tools = await discoverTools(discovery.buildRequest(context));
      setDiscoveredTools(tools);
      setSelectedToolNames([]);
      setToolSearch("");
    } catch (err) {
      setDiscoveryError(err.response?.data?.error || err.message || "Tool discovery failed");
    }
  };

  const handleToggleSelectAllFiltered = () => {
    const filteredNames = filteredDiscoveredTools.map((tool) => tool.name);
    setSelectedToolNames((current) => (
      allFilteredSelected
        ? current.filter((name) => !filteredNames.includes(name))
        : Array.from(new Set([...current, ...filteredNames]))
    ));
  };

  const handleGenerateToolDrafts = async () => {
    const selectedTools = discoveredTools.filter((tool) => selectedToolNames.includes(tool.name));
    const currentDrafts = editingFile
      ? { ...draftContents, [editingFile]: fileContent }
      : draftContents;
    const baseDrafts = generationBaseDrafts || currentDrafts;
    const generated = mergeDiscoveredToolDrafts(baseDrafts.policy, baseDrafts.tools, selectedTools);
    let prompt = baseDrafts.prompt;
    const promptWasEmpty = !prompt.trim();

    setDiscoveryError(null);
    if (promptWasEmpty || regeneratePrompt) {
      if (!values.name.trim()) {
        setDiscoveryError("Enter a display name before generating the prompt.");
        return;
      }
      try {
        setIsGeneratingPrompt(true);
        const result = await generateAgentPrompt({
          agent_name: values.name,
          description: values.description,
          provider: values.provider,
          service_type: values.service_type,
          tools: selectedTools,
        });
        prompt = result.prompt;
      } catch (err) {
        setDiscoveryError(err.response?.data?.error || err.message || "Prompt generation failed");
        return;
      } finally {
        setIsGeneratingPrompt(false);
      }
    }

    const generatedDrafts = { ...generated, prompt };
    const nextDrafts = { ...draftContents, ...generatedDrafts };
    const generatedFileTypes = ["policy", "tools", "prompt"]
      .filter((fileType) => currentDrafts[fileType] !== generatedDrafts[fileType]);
    setGenerationBaseDrafts(baseDrafts);
    if (generatedFileTypes.length) {
      setGenerationRollback((current) => {
        const rollback = current || { drafts: draftContents };
        const pendingFiles = ["policy", "tools", "prompt"]
          .filter((fileType) => generatedDrafts[fileType] !== rollback.drafts[fileType]);
        return pendingFiles.length ? { ...rollback, pendingFiles } : null;
      });
    }
    setDraftContents(nextDrafts);
    setPolicyContent(generated.policy);
    setToolsContent(generated.tools);
    if (editingFile) {
      setFileContent(generatedDrafts[editingFile]);
    }
    // Panel stays open with the selection intact: regenerating re-merges from
    // baseDrafts, so changing the selection and generating again is idempotent.
    setGenerationResult(summarizeDraftChanges(
      currentDrafts,
      { ...currentDrafts, ...generatedDrafts },
      // eslint-disable-next-line no-nested-ternary
      promptWasEmpty ? "prompt generated" : regeneratePrompt ? "prompt regenerated" : "prompt preserved",
    ));
  };

  // Load file content for editing
  const handleLoadFile = (fileType) => {
    setFileError(null);
    setJsonError(null);

    if (editingFile && editingFile !== fileType) {
      setDraftContents((current) => ({ ...current, [editingFile]: fileContent }));
      if (editingFile === "tools") setToolsContent(fileContent);
      if (editingFile === "policy") {
        setPolicyContent(fileContent);
        const connection = readConnectionFromPolicy(fileContent);
        if (connection) {
          connectionRef.current = connection;
          CONNECTION_FIELDS.forEach((field) => setFieldValue(field, connection[field]));
        }
      }
      if (!currentFileHasGeneratedChanges && fileContent !== originalFileContent) {
        setPendingFileBaselines((current) => current[editingFile]
          ? current
          : { ...current, [editingFile]: originalFileContent });
      }
    }

    setFileContent(draftContents[fileType]);
    setOriginalFileContent(pendingFileBaselines[fileType] ?? draftContents[fileType]);
    setEditingFile(fileType);
  };

  // Handle content change for policy and prompt
  const handleFileContentChange = (e) => {
    // Handle both event objects (from textarea) and direct strings (from DualViewEditor form)
    const content = typeof e === 'string' ? e : e.target.value;
    setGenerationBaseDrafts(null);
    setGenerationResult(null);
    setFileContent(content);
    
    // Validate JSON for policy only (tools uses DualViewEditor)
    if (editingFile === 'policy') {
      try {
        JSON.parse(content);
        setJsonError(null);
      } catch (err) {
        setJsonError(`Invalid JSON: ${err.message}`);
      }
    }
  };

  // Handle tools.json changes from DualViewEditor
  const handleToolsChange = (content) => {
    setGenerationBaseDrafts(null);
    setGenerationResult(null);
    setFileContent(content);
  };

  // Save edited file
  const handleSaveFile = () => {
    if (editingFile !== 'prompt' && jsonError) return;
    setFileError(null);
    if (generationRollback?.pendingFiles.includes(editingFile)) {
      const pendingFiles = generationRollback.pendingFiles.filter((fileType) => fileType !== editingFile);
      if (pendingFiles.length) {
        setGenerationRollback({
          ...generationRollback,
          drafts: { ...generationRollback.drafts, [editingFile]: fileContent },
          pendingFiles,
        });
        setGenerationBaseDrafts((current) => current
          ? { ...current, [editingFile]: fileContent }
          : current);
      } else {
        setGenerationRollback(null);
        setGenerationBaseDrafts(null);
        setGenerationResult(null);
      }
    } else if (!generationRollback) {
      setGenerationBaseDrafts(null);
      setGenerationResult(null);
    }
    setDraftContents((current) => ({ ...current, [editingFile]: fileContent }));
    setPendingFileBaselines((current) => {
      const next = { ...current };
      delete next[editingFile];
      return next;
    });
    if (editingFile === 'tools') setToolsContent(fileContent);
    if (editingFile === 'policy') {
      setPolicyContent(fileContent);
      const connection = readConnectionFromPolicy(fileContent);
      if (connection) {
        connectionRef.current = connection;
        CONNECTION_FIELDS.forEach((field) => setFieldValue(field, connection[field]));
      }
    }
    setEditingFile(null);
    setFileContent("");
  };

  const handleCancelEdit = () => {
    if (generationRollback && currentFileHasGeneratedChanges) {
      const restoredContent = generationRollback.drafts[editingFile];
      const pendingFiles = generationRollback.pendingFiles.filter((fileType) => fileType !== editingFile);
      setDraftContents((current) => ({ ...current, [editingFile]: restoredContent }));
      if (editingFile === "policy") setPolicyContent(restoredContent);
      if (editingFile === "tools") setToolsContent(restoredContent);
      if (pendingFiles.length) {
        setGenerationRollback({ ...generationRollback, pendingFiles });
      } else {
        setGenerationRollback(null);
        setGenerationBaseDrafts(null);
        setGenerationResult(null);
      }
    } else if (Object.prototype.hasOwnProperty.call(pendingFileBaselines, editingFile)) {
      const restoredContent = pendingFileBaselines[editingFile];
      setDraftContents((current) => ({ ...current, [editingFile]: restoredContent }));
      if (editingFile === "policy") {
        setPolicyContent(restoredContent);
        const connection = readConnectionFromPolicy(restoredContent);
        if (connection) {
          connectionRef.current = connection;
          CONNECTION_FIELDS.forEach((field) => setFieldValue(field, connection[field]));
        }
      }
      if (editingFile === "tools") setToolsContent(restoredContent);
      setPendingFileBaselines((current) => {
        const next = { ...current };
        delete next[editingFile];
        return next;
      });
    }
    setEditingFile(null);
    setFileContent("");
    setJsonError(null);
    setFileError(null);
  };

  const handleDeleteAgent = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      if (hasPendingEditorTransaction) {
        throw new Error(`Apply or discard the ${editingFile} changes before saving the agent`);
      }
      await deleteAgent(agentId);
      await queryClient.invalidateQueries({
        queryKey: ["get-agents", { org_id: orgId }],
      });
      navigate(ROUTES.FORGE.ROOT);
    } catch (err) {
      const responseData = err.response?.data;
      const dependentAgentIds = responseData?.dependent_agent_ids;
      setDeleteError(
        dependentAgentIds?.length
          ? `${responseData.error}: ${dependentAgentIds.join(', ')}`
          : responseData?.error || err.message || "Failed to delete agent"
      );
      setIsDeleting(false);
    }
  };

  async function handleSubmit(formValues) {
    setIsSubmitting(true);
    setError(null);

    try {
      const connectionProtocol = getConnectionProtocol(connectionType);
      const connectionDraft = connectionDrafts[connectionType] || null;
      if (!connectionProtocol.isReady(connectionDraft)) {
        throw new Error(connectionProtocol.readinessError("saving"));
      }
      // Parse triggers from comma-separated string
      const triggersArray = formValues.triggers
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .map((t) => (t.startsWith("@") ? t : `@${t}`));

      // effectiveDrafts reads the open editor's buffer, which bypasses the editor's
      // own Apply gate — so re-check validity here before it reaches the server.
      if (editingFile && jsonError) {
        throw new Error(`Fix the ${editingFile} validation errors before saving`);
      }
      const effectiveDrafts = editingFile
        ? { ...draftContents, [editingFile]: fileContent }
        : draftContents;
      if (!effectiveDrafts.policy || !effectiveDrafts.tools || !effectiveDrafts.prompt) {
        throw new Error("Policy, Tools, and Prompt drafts are required");
      }
      const nextPolicy = writeConnectionToPolicy(
        effectiveDrafts.policy,
        pickConnection(formValues),
        formValues.provider
      );
      const connectionSource = connectionProtocol.serializeSource(connectionDraft);
      const originalConnectionSource = getConnectionProtocol(
        originalConnectionType
      ).serializeSource(originalConnectionDraft);
      const connectionAction = connectionType !== originalConnectionType
        ? (connectionType === "mcp" ? "remove" : "replace")
        : (JSON.stringify(connectionSource) === JSON.stringify(originalConnectionSource)
          ? "retain"
          : "replace");

      await updateAgentFromDraft(agentId, {
        name: formValues.name,
        triggers: triggersArray,
        provider: formValues.provider,
        service_type: formValues.service_type,
        description: formValues.description,
        enabled: formValues.enabled,
      }, {
        ...effectiveDrafts,
        policy: nextPolicy,
        expectedUpdatedAt: agentAudit?.updated_at || null,
        iconAction,
        icon: iconFile,
        connectionType,
        connectionAction,
        connectionSource: connectionAction === "replace" ? connectionSource : null,
      });
      await queryClient.invalidateQueries({
        queryKey: ["get-agents", { org_id: orgId }],
      });
      navigate(ROUTES.FORGE.ROOT);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || "Failed to update agent");
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="w-full">
        <Header type="forge" />
        <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <Loader variant="form" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Header type="forge" />
      <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
        <div className="max-w-7xl mx-auto px-8 py-6">
          {/* Main Content Container */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-8">
            {/* Header section */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                  Edit Agent
                </h1>
                <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mt-1">
                  Update agent configuration
                </p>
              </div>
              <div className="flex items-center gap-3">
                <SecondaryButton
                  type="button"
                  onClick={() => navigate(ROUTES.FORGE.ROOT)}
                  className="px-4 py-2"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  form="edit-agent-form"
                  disabled={isSubmitting || hasPendingEditorTransaction
                    || !getConnectionProtocol(connectionType).isReady(
                      connectionDrafts[connectionType] || null
                    )
                    || !originalValues || !originalDraftContents || (
                    !isDirty(values, originalValues)
                    && JSON.stringify(draftContents) === JSON.stringify(originalDraftContents)
                    && iconAction === "retain"
                    && connectionType === originalConnectionType
                    && JSON.stringify(connectionDrafts[connectionType] || null) === JSON.stringify(originalConnectionDraft)
                  )}
                  className="px-6 py-2"
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </PrimaryButton>
              </div>
            </div>

            <hr className="border-edge-gray-200 dark:border-edge-gray-700 mb-8" />

            {error && <AlertBanner className="mb-6">{error}</AlertBanner>}

            <form id="edit-agent-form" aria-label="Edit agent" onSubmit={formik.handleSubmit}>
              <div className="space-y-8">
                {/* Section 1: Basic Information */}
                <div>
                  <h3 className={SECTION_HEADING_CLASS}>Basic Information</h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Agent ID
                        </label>
                        <Input
                          type="text"
                          name="agent_id"
                          value={values.agent_id}
                          disabled
                        />
                        <p className={HELP_TEXT_CLASS}>Cannot be changed</p>
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Display Name <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., Google Drive Agent"
                          error={Boolean(errors.name && (touched.name || submitCount > 0))}
                        />
                        {errors.name && (touched.name || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.name}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Provider
                        </label>
                        {loadingProviders ? (
                          <div className="flex items-center gap-2 text-sm text-ink-gray-500 py-2.5">
                            <InlineSpinner />
                            Loading providers...
                          </div>
                        ) : (
                          <Input
                            value={values.provider
                              ? providers.find((p) => p.provider_id === values.provider)?.name || values.provider
                              : "No provider selected"}
                            disabled
                          />
                        )}
                        <p className={HELP_TEXT_CLASS}>Provider cannot be changed in edit mode</p>
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Service Type
                        </label>
                        <Input
                          type="text"
                          name="service_type"
                          value={values.service_type}
                          placeholder="Auto-filled from provider"
                          disabled
                        />
                        <p className={HELP_TEXT_CLASS}>Inherited from selected provider</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Agent Details */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>Agent Details</h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Triggers <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="triggers"
                          value={values.triggers}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="@drive, @gdrive, @files"
                          error={Boolean(errors.triggers && (touched.triggers || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>Comma-separated (@ prefix added automatically)</p>
                        {errors.triggers && (touched.triggers || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.triggers}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Icon
                        </label>
                        <FileUploadInput
                          orgId={orgId}
                          uploadType="agent_icon"
                          entityId={values.agent_id}
                          providerId={values.provider}
                          accept=".svg,.png,.jpg,.jpeg,.webp"
                          initialFilePath={values.icon_path}
                          placeholder="Drop icon or click to browse"
                          deferUpload
                          onFileSelected={(file) => {
                            setIconFile(file);
                            setIconAction(file ? "replace" : "remove");
                          }}
                        />
                        <p className={HELP_TEXT_CLASS}>SVG, PNG, JPG or WebP</p>
                      </div>
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Description
                      </label>
                      <TextArea
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        placeholder="Brief description of what this agent does..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Connection */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>Connection</h3>
                  <AgentConnectionSection
                    value={connectionType}
                    onChange={handleConnectionTypeChange}
                    connectionDrafts={connectionDrafts}
                    onConnectionDraftChange={handleProtocolDraftChange}
                  >
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          MCP Connection
                        </label>
                        <Input
                          type="text"
                          name="mcp_connection"
                          value={values.mcp_connection}
                          onChange={(e) => handleConnectionChange("mcp_connection", e.target.value)}
                          onBlur={handleBlur}
                          placeholder="https://mcp.example.com/mcp"
                          disabled={!policyContent}
                          error={Boolean(errors.mcp_connection && (touched.mcp_connection || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>
                          {policyContent ? "Leave blank for agents that only delegate to other agents" : "Loading policy.json…"}
                        </p>
                        {errors.mcp_connection && (touched.mcp_connection || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.mcp_connection}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  </AgentConnectionSection>
                  <AgentToolDiscoveryPanel
                    canDiscover={getConnectionProtocol(connectionType).discovery.canRun({
                      provider: values.provider,
                      connection: pickConnection(values),
                      draft: connectionDrafts[connectionType] || null,
                    })}
                    isDiscovering={isDiscovering}
                    discoveryProgressLabel={discoveryProgressLabel}
                    onDiscover={handleDiscoverTools}
                    error={discoveryError}
                    tools={discoveredTools}
                    filteredTools={filteredDiscoveredTools}
                    selectedNames={selectedToolNames}
                    search={toolSearch}
                    onSearchChange={setToolSearch}
                    onToggleTool={(toolName, checked) => setSelectedToolNames((current) => (
                      checked ? [...current, toolName] : current.filter((name) => name !== toolName)
                    ))}
                    onToggleAll={handleToggleSelectAllFiltered}
                    allFilteredSelected={allFilteredSelected}
                    someFilteredSelected={someFilteredSelected}
                    onCancel={resetDiscovery}
                    onGenerate={handleGenerateToolDrafts}
                    isGenerating={isGeneratingPrompt}
                    canRegeneratePrompt={Boolean(promptDraftForGeneration.trim())}
                    regeneratePrompt={regeneratePrompt}
                    onRegeneratePromptChange={setRegeneratePrompt}
                    result={generationResult}
                  />
                  <div className="mt-6">
                    <AgentAuthSection
                      values={values}
                      onChange={handleConnectionPatch}
                      disabled={!policyContent}
                    />
                  </div>
                </div>

                {/* Section 4: Configuration Details */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>
                    Configuration Details
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Policy <span className="text-ink-red-500">*</span>
                        <DraftStatusBadge status={draftStatus("policy")} />
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={values.policy_path}
                          disabled
                          className="flex-1"
                        />
                        {values.policy_path && (
                          <CompactButton type="button" onClick={() => handleLoadFile('policy')} disabled={editingFile === 'policy' || Boolean(jsonError)} className="h-[39px]">
                            <Pencil className="w-4 h-4" />
                            Edit
                          </CompactButton>
                        )}
                      </div>
                      <p className={HELP_TEXT_CLASS}>Defines agent governance policies & rate limits</p>
                      {errors.policy_path && submitCount > 0 && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.policy_path}</p>
                      )}
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Tools <span className="text-ink-red-500">*</span>
                        <DraftStatusBadge status={draftStatus("tools")} />
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={values.tools_path}
                          disabled
                          className="flex-1"
                        />
                        {values.tools_path && (
                          <CompactButton type="button" onClick={() => handleLoadFile('tools')} disabled={editingFile === 'tools' || Boolean(jsonError)} className="h-[39px]">
                            <Pencil className="w-4 h-4" />
                            Edit
                          </CompactButton>
                        )}
                      </div>
                      <p className={HELP_TEXT_CLASS}>Define MCP tools & agents</p>
                      {errors.tools_path && submitCount > 0 && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.tools_path}</p>
                      )}
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Prompt <span className="text-ink-red-500">*</span>
                        <DraftStatusBadge status={draftStatus("prompt")} />
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={values.prompt_path}
                          disabled
                          className="flex-1"
                        />
                        {values.prompt_path && (
                          <CompactButton type="button" onClick={() => handleLoadFile('prompt')} disabled={editingFile === 'prompt' || Boolean(jsonError)} className="h-[39px]">
                            <Pencil className="w-4 h-4" />
                            Edit
                          </CompactButton>
                        )}
                      </div>
                      <p className={HELP_TEXT_CLASS}>System prompt for the agent</p>
                      {errors.prompt_path && submitCount > 0 && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.prompt_path}</p>
                      )}
                    </div>
                  </div>
                  {fileError && <p className="text-xs text-ink-red-500 mt-2">{fileError}</p>
                  }
                </div>

                {/* File Editor (shown when editing) */}
                {editingFile && (
                  <FileEditorSection
                    fileType={editingFile}
                    fileContent={fileContent}
                    originalFileContent={originalFileContent}
                    isDirty={editorIsDirty}
                    onChange={editingFile === 'tools' ? handleToolsChange : handleFileContentChange}
                    onSave={handleSaveFile}
                    onCancel={handleCancelEdit}
                    isSaving={false}
                    saveLabel="Apply"
                    cancelLabel="Discard"
                    jsonError={jsonError}
                    fileChangeDetection={fileChangeDetection}
                    availableAgents={availableAgents}
                    currentAgentId={values.agent_id}
                    onChangeDetected={setFileChangeDetection}
                    policyAtom={policyAtom}
                    connectionManagedExternally
                  />
                )}

                {/* Skills — expert agents only */}
                {!hasPendingEditorTransaction && !isSwarmAgent && values.tools_path && (
                  <AgentSkillsSection
                    toolsJsonContent={toolsContent}
                    onChange={(newContent) => {
                      setGenerationBaseDrafts(null);
                      setToolsContent(newContent);
                      setDraftContents((current) => ({ ...current, tools: newContent }));
                    }}
                    orgId={orgId}
                  />
                )}

                {/* Status */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <Toggle
                          checked={values.enabled}
                          onChange={(nextValue) => setFieldValue("enabled", nextValue)}
                        />
                        <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                          Published
                        </label>
                      </div>
                      <p className={HELP_TEXT_CLASS}>
                        Unpublished agents are hidden from the catalog and cannot be used in chat. Admins can still find them in Forge.
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <CompactDangerButton
                        type="button"
                        onClick={() => { setDeleteError(null); setShowDeleteModal(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Agent
                      </CompactDangerButton>
                      <p className={HELP_TEXT_CLASS}>Permanently deletes this agent and all its configuration</p>
                    </div>
                  </div>
                </div>

                <EntityAuditStrip auditInfo={agentAudit} />
              </div>
            </form>
          </div>
        </div>

          <ConfirmDialog
            open={showDeleteModal}
            title="Delete Agent?"
            error={deleteError}
            confirmLabel="Delete"
            loadingLabel="Deleting..."
            isLoading={isDeleting}
            onCancel={() => { setShowDeleteModal(false); setDeleteError(null); }}
            onConfirm={handleDeleteAgent}
          >
            Are you sure you want to delete &ldquo;{values.name}&rdquo;? This action cannot be undone.
          </ConfirmDialog>
      </div>
    </div>
  );
};

export default EditAgentPage;
